/**
 * Theme Provider
 * Manages light/dark theme state with localStorage persistence
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  applyThemeRaw: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'zrm-theme'
const THEME_COLOR_LIGHT = '#f8fafc'
const THEME_COLOR_DARK = '#1a2632'

function getInitialTheme(): Theme {
  // Check localStorage first (already applied by inline script in index.html)
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'dark') return 'dark'
  return 'light'
}

function updateMetaThemeColor(theme: Theme) {
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'light' ? THEME_COLOR_LIGHT : THEME_COLOR_DARK)
  }
}

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme)

  const setTheme = useCallback((newTheme: Theme) => {
    // Add transition class for smooth color change
    document.documentElement.classList.add('theme-transition')

    // Update theme class
    document.documentElement.classList.remove('theme-light', 'theme-dark')
    document.documentElement.classList.add(`theme-${newTheme}`)

    // Update meta theme-color for mobile browsers
    updateMetaThemeColor(newTheme)

    // Persist to localStorage
    localStorage.setItem(STORAGE_KEY, newTheme)

    // Update state
    setThemeState(newTheme)

    // Remove transition class after animation completes
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition')
    }, 300)
  }, [])

  // Swap theme without the transition class — used by ThemeToggle's wipe overlay
  const applyThemeRaw = useCallback((newTheme: Theme) => {
    document.documentElement.classList.remove('theme-light', 'theme-dark')
    document.documentElement.classList.add(`theme-${newTheme}`)
    updateMetaThemeColor(newTheme)
    localStorage.setItem(STORAGE_KEY, newTheme)
    setThemeState(newTheme)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }, [theme, setTheme])

  // Initialize theme on mount
  useEffect(() => {
    // Ensure the correct class is set (matches inline script)
    document.documentElement.classList.add(`theme-${theme}`)
    updateMetaThemeColor(theme)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, applyThemeRaw }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
